import { z } from "zod";

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i, "is not a #rrggbb colour");

export const CARD_INKS = ["accent", "text", "muted", "track", "panel"] as const;

export type CardInk = (typeof CARD_INKS)[number];

const cardColourSchema = z.union([z.enum(CARD_INKS), hexColor]);

export type CardColour = z.infer<typeof cardColourSchema>;

export const CARD_FONTS = [
  "sans",
  "mono",
  "rounded",
  "serif",
  "condensed",
  "geometric",
  "pixel",
] as const;

export type CardFont = (typeof CARD_FONTS)[number];

export const CARD_FONT_WEIGHTS = [400, 500, 600, 700] as const;

export type CardFontWeight = (typeof CARD_FONT_WEIGHTS)[number];

const fontWeightSchema = z.union(CARD_FONT_WEIGHTS.map((weight) => z.literal(weight)));

export const CARD_PLACEHOLDERS = [
  "name",
  "level",
  "prestige",
  "rank",
  "members",
  "xp",
  "levelXp",
  "levelGoal",
  "percent",
  "text",
  "voice",
  "levelLabel",
  "prestigeLabel",
  "rankLabel",
  "progressLabel",
  "xpLabel",
  "textLabel",
  "voiceLabel",
  "previousLevel",
  "reward",
  "levelUpLabel",
] as const;

export type CardPlaceholder = (typeof CARD_PLACEHOLDERS)[number];

export const CARD_STATS = ["level", "prestige", "rank", "levelXp", "xp", "text", "voice"] as const;

export type CardStat = (typeof CARD_STATS)[number];

const CARD_BACKGROUNDS = ["solid", "gradient", "image", "none"] as const;

export type CardBackgroundKind = (typeof CARD_BACKGROUNDS)[number];

const cardBackgroundSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("solid"), color: hexColor }),
  z.strictObject({
    kind: z.literal("gradient"),
    from: hexColor,
    to: hexColor,
    angle: z.number().int().min(0).max(359),
  }),
  z.strictObject({ kind: z.literal("image"), assetId: z.uuid(), dim: z.number().min(0).max(0.8) }),
  z.strictObject({ kind: z.literal("none") }),
]);

export type CardBackground = z.infer<typeof cardBackgroundSchema>;

export const CARD_SIZE_LIMITS = { width: [240, 1200], height: [64, 800] } as const;

export const CARD_EXTENT = 2400;

export const CARD_MAX_ELEMENTS = 40;

export const CARD_MAX_PICTURES = 8;

const coordinate = z.number().int().min(-CARD_EXTENT).max(CARD_EXTENT);

const extent = z.number().int().min(1).max(CARD_EXTENT);

const elementIdSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/);

const CARD_CONDITIONS = ["prestige"] as const;

const shadowSchema = z.strictObject({
  x: z.number().int().min(-64).max(64),
  y: z.number().int().min(-64).max(64),
  blur: z.number().int().min(0).max(64),
  colour: cardColourSchema,
  opacity: z.number().min(0).max(1),
});

export type CardShadow = z.infer<typeof shadowSchema>;

const gradientSchema = z.strictObject({
  to: cardColourSchema,
  angle: z.number().int().min(0).max(359),
});

export type CardGradient = z.infer<typeof gradientSchema>;

const shrinkSchema = z.number().min(0.25).max(1);

const trackingSchema = z.number().min(-0.1).max(0.5);

const placed = {
  id: elementIdSchema,
  x: coordinate,
  y: coordinate,
  width: extent,
  height: extent,
  rotation: z.number().int().min(-180).max(180).optional(),
  opacity: z.number().min(0).max(1),
  hidden: z.boolean().optional(),
  when: z.enum(CARD_CONDITIONS).optional(),
  shadow: shadowSchema.optional(),
};

const pictureElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("picture"),
  assetId: z.uuid(),
  fit: z.enum(["cover", "contain"]),
  radius: z.number().int().min(0).max(999),
});

export const AVATAR_FRAMES = ["circle", "rounded", "square"] as const;

const avatarElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("avatar"),
  frame: z.enum(AVATAR_FRAMES),
  border: z.number().int().min(0).max(16),
  borderColour: cardColourSchema,
});

const CARD_ALIGNS = ["start", "center", "end"] as const;

export type CardAlign = (typeof CARD_ALIGNS)[number];

const textRunSchema = z.strictObject({
  text: z.string().min(1).max(120),
  size: z.number().int().min(6).max(200),
  weight: fontWeightSchema,
  colour: cardColourSchema,
  upper: z.boolean().optional(),
  tracking: trackingSchema.optional(),
});

export type CardTextRun = z.infer<typeof textRunSchema>;

const textElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("text"),
  runs: z.array(textRunSchema).min(1).max(4),
  font: z.enum(CARD_FONTS).optional(),
  align: z.enum(CARD_ALIGNS),
  gap: z.number().int().min(0).max(64),
  shrink: shrinkSchema.optional(),
  plate: z
    .strictObject({
      fill: cardColourSchema,
      opacity: z.number().min(0).max(1),
      radius: z.number().int().min(0).max(999),
      inset: z.number().int().min(0).max(64),
    })
    .optional(),
});

export const CARD_MEASURES = ["level", "text"] as const;

const progressElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("progress"),
  shape: z.enum(["bar", "ring"]),
  measure: z.enum(CARD_MEASURES),
  thickness: z.number().int().min(1).max(120),
  fill: cardColourSchema,
  gradient: gradientSchema.optional(),
  track: cardColourSchema,
});

export const CARD_STAT_LAYOUTS = ["columns", "inline", "tiles"] as const;

export type CardStatLayout = (typeof CARD_STAT_LAYOUTS)[number];

const statsElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("stats"),
  items: z
    .array(z.enum(CARD_STATS))
    .min(1)
    .max(CARD_STATS.length)
    .refine((items) => new Set(items).size === items.length, { message: "names a figure twice" }),
  layout: z.enum(CARD_STAT_LAYOUTS),
  font: z.enum(CARD_FONTS).optional(),
  gap: z.number().int().min(0).max(120),
  labelSize: z.number().int().min(6).max(120),
  valueSize: z.number().int().min(6).max(200),
  labelColour: cardColourSchema,
  valueColour: cardColourSchema,
  align: z.enum(CARD_ALIGNS).optional(),
  shrink: shrinkSchema.optional(),
  labelWeight: fontWeightSchema.optional(),
  valueWeight: fontWeightSchema.optional(),
  labelUpper: z.boolean().optional(),
  labelTracking: trackingSchema.optional(),
});

const shapeImageSchema = z.strictObject({
  assetId: z.uuid(),
  fit: z.enum(["cover", "contain"]),
});

export type CardShapeImage = z.infer<typeof shapeImageSchema>;

const shapeElementSchema = z.strictObject({
  ...placed,
  kind: z.literal("shape"),
  shape: z.enum(["rect", "ellipse"]),
  fill: cardColourSchema,
  gradient: gradientSchema.optional(),
  image: shapeImageSchema.optional(),
  radius: z.number().int().min(0).max(999),
  border: z.number().int().min(0).max(32),
  borderColour: cardColourSchema,
});

const cardElementSchema = z.discriminatedUnion("kind", [
  pictureElementSchema,
  avatarElementSchema,
  textElementSchema,
  progressElementSchema,
  statsElementSchema,
  shapeElementSchema,
]);

export type CardElement = z.infer<typeof cardElementSchema>;

export {
  cardBackgroundSchema,
  cardColourSchema,
  elementIdSchema as cardElementIdSchema,
  gradientSchema as cardGradientSchema,
  hexColor as cardHexSchema,
  pictureElementSchema as cardPictureSchema,
  shapeImageSchema as cardShapeImageSchema,
  textRunSchema as cardTextRunSchema,
};

export type CardElementKind = CardElement["kind"];

export type CardElementOf<K extends CardElementKind> = Extract<CardElement, { kind: K }>;

export const cardDesignSchema = z
  .strictObject({
    version: z.literal(2),
    width: z.number().int().min(CARD_SIZE_LIMITS.width[0]).max(CARD_SIZE_LIMITS.width[1]),
    height: z.number().int().min(CARD_SIZE_LIMITS.height[0]).max(CARD_SIZE_LIMITS.height[1]),
    radius: z.number().int().min(0).max(64),
    font: z.enum(CARD_FONTS),
    accent: hexColor,
    text: hexColor,
    background: cardBackgroundSchema,
    elements: z.array(cardElementSchema).max(CARD_MAX_ELEMENTS),
  })
  .superRefine((design, context) => {
    const seen = new Set<string>();
    let pictures = 0;
    design.elements.forEach((element, index) => {
      if (seen.has(element.id)) {
        context.addIssue({
          code: "custom",
          path: ["elements", index, "id"],
          message: "each part needs its own id",
        });
      }
      seen.add(element.id);
      if (!drawsPicture(element)) return;
      pictures += 1;
      if (pictures > CARD_MAX_PICTURES) {
        context.addIssue({
          code: "custom",
          path: ["elements", index],
          message: `a card places at most ${String(CARD_MAX_PICTURES)} pictures`,
        });
      }
    });
  });

export type CardDesign = z.infer<typeof cardDesignSchema>;

const partPictureAsset = (element: CardElement): string | null => {
  if (element.kind === "picture") return element.assetId;
  return element.kind === "shape" && element.image !== undefined ? element.image.assetId : null;
};

export const drawsPicture = (element: CardElement): boolean => partPictureAsset(element) !== null;

export const CARD_PIXEL_RATIO = 2;

export const cardBackgroundAsset = (design: CardDesign): string | null =>
  design.background.kind === "image" ? design.background.assetId : null;

export const cardPictureAssets = (design: CardDesign): readonly string[] =>
  design.elements.flatMap((element) => {
    const assetId = partPictureAsset(element);
    return assetId === null ? [] : [assetId];
  });

interface CardTheme {
  readonly accent: string;
  readonly text: string;
  readonly background: CardBackground;
}

const CARD_THEME_IDS = ["house", "midnight", "forest", "ember", "paper"] as const;

export type CardThemeId = (typeof CARD_THEME_IDS)[number];

const CARD_THEMES: Readonly<Record<CardThemeId, CardTheme>> = {
  house: { accent: "#3987e5", text: "#f4f2ec", background: { kind: "solid", color: "#151514" } },
  midnight: {
    accent: "#8b7cf6",
    text: "#f4f2ec",
    background: { kind: "gradient", from: "#0f1a2e", to: "#1f1433", angle: 135 },
  },
  forest: {
    accent: "#57f287",
    text: "#f4f2ec",
    background: { kind: "gradient", from: "#0f2a24", to: "#151514", angle: 160 },
  },
  ember: {
    accent: "#f0b232",
    text: "#f4f2ec",
    background: { kind: "gradient", from: "#2e1a12", to: "#151514", angle: 160 },
  },
  paper: { accent: "#3987e5", text: "#1e1f22", background: { kind: "solid", color: "#f4f2ec" } },
};

const CARD_STYLE_IDS = ["classic", "ring", "sheet", "strip"] as const;

export type CardStyleId = (typeof CARD_STYLE_IDS)[number];

type CardStyle = Pick<CardDesign, "width" | "height" | "radius" | "font" | "elements">;

const run = (
  text: string,
  size: number,
  weight: CardFontWeight,
  colour: CardColour,
  upper?: true,
): CardTextRun => ({ text, size, weight, colour, ...(upper === undefined ? {} : { upper }) });

const CLASSIC: CardStyle = {
  width: 640,
  height: 168,
  radius: 0,
  font: "sans",
  elements: [
    {
      id: "avatar",
      kind: "avatar",
      x: 24,
      y: 36,
      width: 96,
      height: 96,
      opacity: 1,
      frame: "circle",
      border: 3,
      borderColour: "accent",
    },
    {
      id: "name",
      kind: "text",
      x: 144,
      y: 26,
      width: 300,
      height: 40,
      opacity: 1,
      runs: [run("{name}", 28, 600, "text")],
      align: "start",
      gap: 0,
    },
    {
      id: "level",
      kind: "text",
      x: 452,
      y: 24,
      width: 164,
      height: 44,
      opacity: 1,
      runs: [run("{levelLabel}", 13, 500, "muted"), run("{level}", 34, 700, "accent")],
      align: "end",
      gap: 8,
    },
    {
      id: "figures",
      kind: "stats",
      x: 144,
      y: 76,
      width: 472,
      height: 40,
      opacity: 1,
      items: ["prestige", "rank", "levelXp", "text", "voice"],
      layout: "columns",
      gap: 28,
      labelSize: 12,
      valueSize: 17,
      labelColour: "muted",
      valueColour: "text",
    },
    {
      id: "progress",
      kind: "progress",
      x: 144,
      y: 130,
      width: 472,
      height: 8,
      opacity: 1,
      shape: "bar",
      measure: "level",
      thickness: 8,
      fill: "accent",
      track: "track",
    },
  ],
};

const RING: CardStyle = {
  width: 640,
  height: 200,
  radius: 0,
  font: "sans",
  elements: [
    {
      id: "progress",
      kind: "progress",
      x: 24,
      y: 24,
      width: 152,
      height: 152,
      opacity: 1,
      shape: "ring",
      measure: "level",
      thickness: 10,
      fill: "accent",
      track: "track",
    },
    {
      id: "avatar",
      kind: "avatar",
      x: 44,
      y: 44,
      width: 112,
      height: 112,
      opacity: 1,
      frame: "circle",
      border: 0,
      borderColour: "accent",
    },
    {
      id: "name",
      kind: "text",
      x: 204,
      y: 30,
      width: 412,
      height: 40,
      opacity: 1,
      runs: [run("{name}", 28, 600, "text")],
      align: "start",
      gap: 0,
    },
    {
      id: "level",
      kind: "text",
      x: 204,
      y: 74,
      width: 220,
      height: 68,
      opacity: 1,
      runs: [run("{levelLabel}", 14, 500, "muted", true), run("{level}", 56, 700, "accent")],
      align: "start",
      gap: 10,
    },
    {
      id: "rank",
      kind: "text",
      x: 424,
      y: 86,
      width: 192,
      height: 48,
      opacity: 1,
      runs: [run("{rank}", 36, 700, "text"), run("/ {members}", 14, 500, "muted")],
      align: "end",
      gap: 6,
    },
    {
      id: "figures",
      kind: "stats",
      x: 204,
      y: 150,
      width: 412,
      height: 22,
      opacity: 1,
      items: ["prestige", "levelXp", "text", "voice"],
      layout: "inline",
      gap: 20,
      labelSize: 13,
      valueSize: 13,
      labelColour: "muted",
      valueColour: "text",
    },
  ],
};

const SHEET: CardStyle = {
  width: 640,
  height: 320,
  radius: 0,
  font: "sans",
  elements: [
    {
      id: "avatar",
      kind: "avatar",
      x: 28,
      y: 28,
      width: 72,
      height: 72,
      opacity: 1,
      frame: "rounded",
      border: 0,
      borderColour: "accent",
    },
    {
      id: "name",
      kind: "text",
      x: 120,
      y: 30,
      width: 492,
      height: 36,
      opacity: 1,
      runs: [run("{name}", 26, 600, "text")],
      align: "start",
      gap: 0,
    },
    {
      id: "xp",
      kind: "text",
      x: 120,
      y: 68,
      width: 492,
      height: 26,
      opacity: 1,
      runs: [run("{xpLabel}", 14, 500, "muted"), run("{xp}", 16, 600, "accent")],
      align: "start",
      gap: 6,
    },
    {
      id: "figures",
      kind: "stats",
      x: 28,
      y: 124,
      width: 584,
      height: 76,
      opacity: 1,
      items: ["level", "prestige", "rank"],
      layout: "tiles",
      gap: 12,
      labelSize: 12,
      valueSize: 24,
      labelColour: "muted",
      valueColour: "text",
    },
    {
      id: "text",
      kind: "text",
      x: 28,
      y: 220,
      width: 292,
      height: 20,
      opacity: 1,
      runs: [run("{textLabel}", 12, 500, "muted"), run("{text}", 13, 600, "text")],
      align: "start",
      gap: 6,
    },
    {
      id: "voice",
      kind: "text",
      x: 320,
      y: 220,
      width: 292,
      height: 20,
      opacity: 1,
      runs: [run("{voiceLabel}", 12, 500, "muted"), run("{voice}", 13, 600, "text")],
      align: "end",
      gap: 6,
    },
    {
      id: "split",
      kind: "progress",
      x: 28,
      y: 246,
      width: 584,
      height: 8,
      opacity: 1,
      shape: "bar",
      measure: "text",
      thickness: 8,
      fill: "accent",
      track: "track",
    },
    {
      id: "progress-label",
      kind: "text",
      x: 28,
      y: 264,
      width: 584,
      height: 20,
      opacity: 1,
      runs: [
        run("{levelXp}", 13, 600, "text"),
        run("/ {levelGoal}", 13, 600, "text"),
        run("{percent}", 12, 500, "muted"),
      ],
      align: "start",
      gap: 6,
    },
    {
      id: "progress",
      kind: "progress",
      x: 28,
      y: 290,
      width: 584,
      height: 8,
      opacity: 1,
      shape: "bar",
      measure: "level",
      thickness: 8,
      fill: "accent",
      track: "track",
    },
  ],
};

const STRIP: CardStyle = {
  width: 640,
  height: 80,
  radius: 0,
  font: "sans",
  elements: [
    {
      id: "avatar",
      kind: "avatar",
      x: 16,
      y: 14,
      width: 48,
      height: 48,
      opacity: 1,
      frame: "circle",
      border: 0,
      borderColour: "accent",
    },
    {
      id: "name",
      kind: "text",
      x: 80,
      y: 12,
      width: 340,
      height: 28,
      opacity: 1,
      runs: [run("{name}", 20, 600, "text")],
      align: "start",
      gap: 0,
    },
    {
      id: "level",
      kind: "text",
      x: 420,
      y: 10,
      width: 204,
      height: 32,
      opacity: 1,
      runs: [run("{levelLabel}", 12, 500, "muted"), run("{level}", 24, 700, "accent")],
      align: "end",
      gap: 6,
    },
    {
      id: "figures",
      kind: "stats",
      x: 80,
      y: 42,
      width: 544,
      height: 20,
      opacity: 1,
      items: ["prestige", "rank", "levelXp", "text", "voice"],
      layout: "inline",
      gap: 18,
      labelSize: 12,
      valueSize: 12,
      labelColour: "muted",
      valueColour: "text",
    },
    {
      id: "progress",
      kind: "progress",
      x: 0,
      y: 76,
      width: 640,
      height: 4,
      opacity: 1,
      shape: "bar",
      measure: "level",
      thickness: 4,
      fill: "accent",
      track: "track",
    },
  ],
};

const CARD_STYLES: Readonly<Record<CardStyleId, CardStyle>> = {
  classic: CLASSIC,
  ring: RING,
  sheet: SHEET,
  strip: STRIP,
};

const styledCard = (style: CardStyleId, theme: CardThemeId): CardDesign =>
  structuredClone({ version: 2, ...CARD_STYLES[style], ...CARD_THEMES[theme] });

export const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, inner: unknown) =>
    inner !== null && typeof inner === "object" && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : inner,
  );

const cardStyleOf = (
  design: CardDesign,
): { readonly style: CardStyleId; readonly theme: CardThemeId } | null => {
  const drawn = canonicalJson(design);
  for (const style of CARD_STYLE_IDS) {
    for (const theme of CARD_THEME_IDS) {
      if (canonicalJson(styledCard(style, theme)) === drawn) return { style, theme };
    }
  }
  return null;
};

export const RANK_CARD_LAYOUTS = ["horizontal", "profile", "banner"] as const;

export const LEVEL_UP_LAYOUTS = ["level-up-wide", "level-up-square"] as const;

export const CARD_LAYOUTS = [...RANK_CARD_LAYOUTS, ...LEVEL_UP_LAYOUTS] as const;

export type CardLayout = (typeof CARD_LAYOUTS)[number];

export type RankCardLayout = (typeof RANK_CARD_LAYOUTS)[number];

export const CARD_MODES = ["dark", "light"] as const;

export type CardMode = (typeof CARD_MODES)[number];

export interface CardLook {
  readonly layout: CardLayout;
  readonly accent: string;
  readonly mode: CardMode;
}

export const DEFAULT_CARD_LOOK: CardLook = {
  layout: "horizontal",
  accent: "#3987e5",
  mode: "dark",
};

export const DEFAULT_LEVEL_UP_LOOK: CardLook = { ...DEFAULT_CARD_LOOK, layout: "level-up-wide" };

interface CardGround {
  readonly text: string;
  readonly surface: string;
  readonly edge: string;
  readonly background: CardBackground;
}

const GROUNDS: Readonly<Record<CardMode, CardGround>> = {
  dark: {
    text: "#ededed",
    surface: "#0a0a0a",
    edge: "#262626",
    background: { kind: "solid", color: "#0a0a0a" },
  },
  light: {
    text: "#171717",
    surface: "#ffffff",
    edge: "#e5e5e5",
    background: { kind: "solid", color: "#ffffff" },
  },
};

type LayoutFrame = Pick<
  CardDesign,
  "width" | "height" | "radius" | "font" | "background" | "elements"
>;

type Box = readonly [number, number, number, number];

const surface = (ground: CardGround, width: number, height: number): CardElement => ({
  id: "surface",
  kind: "shape",
  shape: "rect",
  x: 0,
  y: 0,
  width,
  height,
  opacity: 1,
  fill: ground.surface,
  radius: 16,
  border: 1,
  borderColour: ground.edge,
});

const words = (
  id: string,
  [x, y, width, height]: Box,
  runs: readonly CardTextRun[],
  extra: Partial<CardElementOf<"text">> = {},
): CardElement => ({
  id,
  kind: "text",
  x,
  y,
  width,
  height,
  opacity: 1,
  runs: [...runs],
  align: "start",
  gap: 6,
  shrink: 0.7,
  ...extra,
});

const ink = (
  text: string,
  size: number,
  weight: CardFontWeight,
  colour: CardColour,
  extra: Partial<CardTextRun> = {},
): CardTextRun => ({ text, size, weight, colour, ...extra });

const avatar = ([x, y, width, height]: Box, border = 0): CardElementOf<"avatar"> => ({
  id: "avatar",
  kind: "avatar",
  x,
  y,
  width,
  height,
  opacity: 1,
  frame: "circle",
  border,
  borderColour: "accent",
});

const bar = ([x, y, width, height]: Box): CardElement => ({
  id: "progress",
  kind: "progress",
  x,
  y,
  width,
  height,
  opacity: 1,
  shape: "bar",
  measure: "level",
  thickness: height,
  fill: "accent",
  track: "track",
});

const rule = (
  ground: CardGround,
  [x, y, width]: readonly [number, number, number],
): CardElement => ({
  id: "rule",
  kind: "shape",
  shape: "rect",
  x,
  y,
  width,
  height: 1,
  opacity: 1,
  fill: ground.edge,
  radius: 0,
  border: 0,
  borderColour: "track",
});

const progressRow = (
  [x, y, width]: readonly [number, number, number],
  figures: "end" | "start",
): readonly CardElement[] => [
  ...(figures === "end"
    ? [
        words("progress-label", [x, y, width / 2, 18], [ink("{progressLabel}", 13, 500, "muted")]),
        words(
          "progress-figures",
          [x + width / 2, y, width / 2, 18],
          [ink("{levelXp}", 13, 500, "text"), ink("/ {levelGoal}", 13, 500, "muted")],
          { align: "end", gap: 4 },
        ),
      ]
    : [
        words(
          "progress-figures",
          [x, y, width / 2, 18],
          [
            ink("{progressLabel}", 13, 500, "muted"),
            ink("{levelXp}", 13, 500, "text"),
            ink("/ {levelGoal}", 13, 500, "muted"),
          ],
          { gap: 4 },
        ),
      ]),
  bar([x, y + 28, width, 6]),
];

const rankLine = (box: Box, align: CardAlign): CardElement =>
  words(
    "rank",
    box,
    [
      ink("{rankLabel}", 14, 500, "muted"),
      ink("{rank}", 14, 600, "text"),
      ink("/ {members}", 14, 500, "muted"),
    ],
    { align, gap: 6, shrink: 0.8 },
  );

const ringed = (
  [x, y, size]: readonly [number, number, number],
  thickness: number,
): readonly CardElement[] => {
  const inset = thickness + 8;
  return [
    {
      id: "progress",
      kind: "progress",
      x,
      y,
      width: size,
      height: size,
      opacity: 1,
      shape: "ring",
      measure: "level",
      thickness,
      fill: "accent",
      track: "track",
    },
    avatar([x + inset, y + inset, size - inset * 2, size - inset * 2]),
  ];
};

const figures = (
  [x, y, width, height]: Box,
  align: CardAlign | undefined,
  valueSize: number,
): CardElement => ({
  id: "figures",
  kind: "stats",
  x,
  y,
  width,
  height,
  opacity: 1,
  items: ["level", "prestige", "rank", "levelXp"],
  layout: "columns",
  ...(align === undefined ? {} : { align }),
  gap: align === undefined ? 32 : 16,
  labelSize: 13,
  valueSize,
  labelColour: "muted",
  valueColour: "text",
  labelWeight: 500,
  valueWeight: 600,
  shrink: 0.6,
});

const horizontal = (ground: CardGround): LayoutFrame => ({
  width: 600,
  height: 200,
  radius: 16,
  font: "sans",
  background: { kind: "none" },
  elements: [
    surface(ground, 600, 200),
    ...ringed([28, 28, 144], 6),
    words("name", [204, 40, 368, 40], [ink("{name}", 30, 600, "text", { tracking: -0.02 })], {
      shrink: 0.55,
    }),
    rule(ground, [204, 92, 368]),
    figures([204, 108, 368, 52], "start", 24),
  ],
});

const profile = (ground: CardGround): LayoutFrame => ({
  width: 400,
  height: 352,
  radius: 16,
  font: "sans",
  background: { kind: "none" },
  elements: [
    surface(ground, 400, 352),
    ...ringed([128, 32, 144], 6),
    words("name", [32, 196, 336, 36], [ink("{name}", 26, 600, "text", { tracking: -0.02 })], {
      align: "center",
      shrink: 0.6,
    }),
    rule(ground, [32, 252, 336]),
    figures([32, 268, 336, 52], "center", 22),
  ],
});

const banner = (ground: CardGround): LayoutFrame => ({
  width: 400,
  height: 352,
  radius: 16,
  font: "sans",
  background: { kind: "none" },
  elements: [
    surface(ground, 400, 352),
    {
      id: "band",
      kind: "shape",
      shape: "rect",
      x: 1,
      y: 1,
      width: 398,
      height: 112,
      opacity: 1,
      fill: "accent",
      gradient: { to: ground.surface, angle: 180 },
      radius: 15,
      border: 0,
      borderColour: "track",
    },
    {
      ...avatar([28, 64, 104, 104]),
      border: 4,
      borderColour: ground.surface,
    },
    words(
      "prestige",
      [232, 20, 148, 22],
      [ink("{prestigeLabel}", 12, 500, "text"), ink("{prestige}", 12, 600, "text")],
      {
        when: "prestige",
        align: "end",
        gap: 4,
        plate: { fill: ground.surface, opacity: 0.85, radius: 999, inset: 10 },
      },
    ),
    words("name", [32, 184, 232, 32], [ink("{name}", 24, 600, "text", { tracking: -0.02 })], {
      shrink: 0.6,
    }),
    rankLine([32, 220, 232, 20], "start"),
    words("level-label", [268, 184, 100, 18], [ink("{levelLabel}", 13, 500, "muted")], {
      align: "end",
    }),
    words("level", [248, 200, 120, 40], [ink("{level}", 34, 600, "text", { tracking: -0.04 })], {
      align: "end",
      shrink: 0.6,
    }),
    rule(ground, [32, 260, 336]),
    ...progressRow([32, 280, 336], "end"),
  ],
});

const wash = (ground: CardGround, width: number, height: number, angle: number): CardElement => ({
  id: "wash",
  kind: "shape",
  shape: "rect",
  x: 1,
  y: 1,
  width: width - 2,
  height: height - 2,
  opacity: 0.2,
  fill: "accent",
  gradient: { to: ground.surface, angle },
  radius: 15,
  border: 0,
  borderColour: "track",
});

const reward = (box: Box, align: CardAlign): CardElement =>
  words("reward", box, [ink("{reward}", 13, 600, "text")], {
    align,
    plate: { fill: "track", opacity: 1, radius: 999, inset: 12 },
  });

const levelUpWide = (ground: CardGround): LayoutFrame => ({
  width: 600,
  height: 200,
  radius: 16,
  font: "sans",
  background: { kind: "none" },
  elements: [
    surface(ground, 600, 200),
    wash(ground, 600, 200, 90),
    avatar([32, 36, 128, 128], 3),
    words(
      "heading",
      [192, 48, 240, 18],
      [ink("{levelUpLabel}", 12, 600, "accent", { upper: true, tracking: 0.08 })],
    ),
    words("name", [192, 70, 240, 38], [ink("{name}", 28, 600, "text", { tracking: -0.02 })], {
      shrink: 0.55,
    }),
    reward([192, 120, 240, 28], "start"),
    words("level-label", [448, 48, 120, 18], [ink("{levelLabel}", 13, 500, "muted")], {
      align: "end",
    }),
    words("level", [432, 66, 136, 84], [ink("{level}", 72, 600, "accent", { tracking: -0.04 })], {
      align: "end",
      shrink: 0.45,
    }),
  ],
});

const levelUpSquare = (ground: CardGround): LayoutFrame => ({
  width: 400,
  height: 420,
  radius: 16,
  font: "sans",
  background: { kind: "none" },
  elements: [
    surface(ground, 400, 420),
    wash(ground, 400, 420, 180),
    avatar([144, 40, 112, 112], 3),
    words(
      "heading",
      [32, 176, 336, 18],
      [ink("{levelUpLabel}", 12, 600, "accent", { upper: true, tracking: 0.08 })],
      { align: "center" },
    ),
    words("level", [32, 198, 336, 96], [ink("{level}", 88, 600, "text", { tracking: -0.04 })], {
      align: "center",
      shrink: 0.5,
    }),
    words("name", [32, 302, 336, 30], [ink("{name}", 22, 600, "text", { tracking: -0.02 })], {
      align: "center",
      shrink: 0.6,
    }),
    reward([32, 348, 336, 28], "center"),
  ],
});

const LAYOUTS: Readonly<Record<CardLayout, (ground: CardGround) => LayoutFrame>> = {
  horizontal,
  profile,
  banner,
  "level-up-wide": levelUpWide,
  "level-up-square": levelUpSquare,
};

export const cardOfLook = (look: CardLook): CardDesign => {
  const ground = GROUNDS[look.mode];
  return structuredClone({
    version: 2,
    ...LAYOUTS[look.layout](ground),
    accent: look.accent,
    text: ground.text,
  });
};

export const lookOf = (design: CardDesign): CardLook | null => {
  const drawn = canonicalJson(design);
  for (const layout of CARD_LAYOUTS) {
    for (const mode of CARD_MODES) {
      const look = { layout, accent: design.accent, mode };
      if (canonicalJson(cardOfLook(look)) === drawn) return look;
    }
  }
  return null;
};

const frameOf = (design: CardDesign): string =>
  canonicalJson({
    width: design.width,
    height: design.height,
    radius: design.radius,
    elements: design.elements,
  });

const FRAMES: ReadonlyMap<string, Omit<CardLook, "accent">> = new Map(
  CARD_LAYOUTS.flatMap((layout) =>
    CARD_MODES.map((mode) => [
      frameOf(cardOfLook({ layout, accent: DEFAULT_CARD_LOOK.accent, mode })),
      { layout, mode },
    ]),
  ),
);

const framingOf = (design: CardDesign): Omit<CardLook, "accent"> | null => {
  const found = FRAMES.get(frameOf(design));
  return found === undefined ? null : found;
};

const isLight = (hex: string): boolean => {
  const channel = (at: number): number => Number.parseInt(hex.slice(at, at + 2), 16) / 255;
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5) > 0.5;
};

const cardModeOf = (design: CardDesign): CardMode => {
  const framing = framingOf(design);
  if (framing !== null) return framing.mode;
  return isLight(design.text) ? "dark" : "light";
};

export const cardGroundOf = (design: CardDesign): CardGround => GROUNDS[cardModeOf(design)];

export const relaidCard = (
  design: CardDesign,
  change: { readonly layout?: CardLayout; readonly mode?: CardMode },
): CardDesign => {
  const framing = framingOf(design);
  const mode = change.mode !== undefined ? change.mode : cardModeOf(design);
  const layout = change.layout !== undefined ? change.layout : framing?.layout;
  if (layout === undefined) {
    if (change.mode === undefined) return design;
    const ground = GROUNDS[change.mode];
    return { ...design, text: ground.text, background: ground.background };
  }
  const look = cardOfLook({ layout, accent: design.accent, mode });
  return {
    ...design,
    width: look.width,
    height: look.height,
    radius: look.radius,
    elements: look.elements,
    background: look.background,
    ...(change.mode === undefined ? {} : { text: look.text }),
  };
};

export const isBuiltInCard = (design: CardDesign): boolean =>
  lookOf(design) !== null || cardStyleOf(design) !== null;

export const DEFAULT_CARD_DESIGN: CardDesign = cardOfLook(DEFAULT_CARD_LOOK);

const LEGACY_LAYOUTS = ["classic", "compact", "spotlight"] as const;

const legacyDesignSchema = z.strictObject({
  layout: z.enum(LEGACY_LAYOUTS),
  font: z.enum(["sans", "mono"]),
  accent: hexColor,
  text: hexColor,
  background: cardBackgroundSchema,
  avatar: z.enum([...AVATAR_FRAMES, "none"]),
  show: z.strictObject({ rank: z.boolean(), xp: z.boolean(), split: z.boolean() }),
  size: z.strictObject({ width: z.number().int(), height: z.number().int() }).optional(),
  layers: z
    .array(
      z.strictObject({
        id: z.string(),
        assetId: z.uuid(),
        x: z.number().int(),
        y: z.number().int(),
        width: z.number().int(),
        height: z.number().int(),
        opacity: z.number(),
        front: z.boolean(),
      }),
    )
    .optional(),
});

type LegacyDesign = z.infer<typeof legacyDesignSchema>;

const LEGACY_STYLE: Readonly<Record<LegacyDesign["layout"], CardStyleId>> = {
  classic: "classic",
  compact: "strip",
  spotlight: "ring",
};

const legacyThemeOf = (legacy: LegacyDesign): CardThemeId | null => {
  const plain =
    legacy.layout === "classic" &&
    legacy.font === "sans" &&
    legacy.avatar === "circle" &&
    legacy.show.rank &&
    legacy.show.xp &&
    legacy.show.split &&
    legacy.size === undefined &&
    (legacy.layers === undefined || legacy.layers.length === 0);
  if (!plain) return null;
  const found = CARD_THEME_IDS.find(
    (id) =>
      canonicalJson(CARD_THEMES[id]) ===
      canonicalJson({ accent: legacy.accent, text: legacy.text, background: legacy.background }),
  );
  return found === undefined ? null : found;
};

const hiddenByLegacy = (legacy: LegacyDesign): ReadonlySet<CardStat> =>
  new Set<CardStat>([
    ...(legacy.show.rank ? [] : (["rank"] as const)),
    ...(legacy.show.xp ? [] : (["levelXp"] as const)),
    ...(legacy.show.split ? [] : (["text", "voice"] as const)),
  ]);

const fromLegacy = (legacy: LegacyDesign): CardDesign => {
  const theme = legacyThemeOf(legacy);
  if (theme !== null) return styledCard("classic", theme);
  const style = CARD_STYLES[LEGACY_STYLE[legacy.layout]];
  const hidden = hiddenByLegacy(legacy);
  const avatar = legacy.avatar;
  const parts = style.elements.flatMap((element): CardElement[] => {
    if (element.kind === "avatar") return avatar === "none" ? [] : [{ ...element, frame: avatar }];
    if (element.kind !== "stats") return [element];
    const items = element.items.filter((item) => !hidden.has(item));
    return items.length === 0 ? [] : [{ ...element, items }];
  });
  const layers = legacy.layers === undefined ? [] : legacy.layers;
  const picture = (layer: (typeof layers)[number]): CardElement => ({
    id: `picture-${String(layers.indexOf(layer) + 1)}`,
    kind: "picture",
    assetId: layer.assetId,
    x: Math.max(-CARD_EXTENT, Math.min(CARD_EXTENT, layer.x)),
    y: Math.max(-CARD_EXTENT, Math.min(CARD_EXTENT, layer.y)),
    width: Math.max(1, Math.min(CARD_EXTENT, layer.width)),
    height: Math.max(1, Math.min(CARD_EXTENT, layer.height)),
    opacity: Math.max(0, Math.min(1, layer.opacity)),
    fit: "cover",
    radius: 0,
  });
  const clamp = (value: number, [low, high]: readonly [number, number]): number =>
    Math.max(low, Math.min(high, value));
  return {
    version: 2,
    width:
      legacy.size === undefined ? style.width : clamp(legacy.size.width, CARD_SIZE_LIMITS.width),
    height:
      legacy.size === undefined ? style.height : clamp(legacy.size.height, CARD_SIZE_LIMITS.height),
    radius: style.radius,
    font: legacy.font,
    accent: legacy.accent,
    text: legacy.text,
    background: legacy.background,
    elements: [
      ...layers.filter((layer) => !layer.front).map(picture),
      ...parts,
      ...layers.filter((layer) => layer.front).map(picture),
    ],
  };
};

export const currentCardDesign = (stored: unknown): unknown => {
  const legacy = legacyDesignSchema.safeParse(stored);
  return legacy.success ? fromLegacy(legacy.data) : stored;
};
